import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';

const PracticeAreaSection = ({ id, index, title, description, approach }) => (
  <div id={id} className="py-12 border-t border-gray-200" data-testid={`practice-area-${id}`}>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-1">
        <span className="text-green font-heading font-bold text-lg">0{index}</span>
      </div>
      <div className="lg:col-span-11">
        <h2 className="font-heading text-xl md:text-2xl text-navy mb-4">{title}</h2>
        <p className="text-gray-600 leading-relaxed mb-4 max-w-3xl">{description}</p>
        <p className="text-gray-500 text-sm leading-relaxed max-w-3xl">{approach}</p>
      </div>
    </div>
  </div>
);

export const PracticeAreasPage = () => {
  return (
    <div data-testid="practice-areas-page">
      {/* Hero Section */}
      <section className="hero-section py-16 md:py-20">
        <div className="container-law">
          <div className="breadcrumb text-white/60 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span>Practice Areas</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">Practice Areas</h1>
          <p className="text-white/80 text-lg max-w-2xl">
            We focus on the commercial agreements that define how life sciences technology companies operate, sell, and grow.
          </p>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="border-b border-gray-200 bg-white sticky top-[81px] z-40">
        <div className="container-law">
          <div className="flex overflow-x-auto hide-scrollbar -mb-px">
            <a href="#saas" className="tab-button">SaaS</a>
            <a href="#msa" className="tab-button">Services</a>
            <a href="#data" className="tab-button">Data</a>
            <a href="#life-sciences" className="tab-button">Life Sciences</a>
            <a href="#ip" className="tab-button">IP</a>
            <a href="#privacy" className="tab-button">Privacy</a>
            <a href="#vendor-risk" className="tab-button">Risk</a>
          </div>
        </div>
      </section>

      {/* Practice Areas List */}
      <section className="py-8">
        <div className="container-law">
          <PracticeAreaSection
            id="saas"
            index={1}
            title="SaaS Agreements & Software Licensing"
            description="We structure and negotiate enterprise software agreements for life sciences technology vendors—master subscription agreements, order forms, and the commercial terms that govern long-term platform relationships with pharmaceutical and biotech customers."
            approach="Our clients often combine SaaS with professional services, data access, and ongoing support. We help build agreement frameworks that reflect these hybrid models while remaining commercially reasonable for enterprise buyers."
          />
          <PracticeAreaSection
            id="msa"
            index={2}
            title="Master Services Agreements"
            description="We draft and negotiate professional services, implementation, and consulting arrangements for technology vendors delivering specialized expertise to life sciences customers."
            approach="Services agreements in this space often involve validation support, regulatory consulting, or clinical operations work. We structure terms that define scope clearly, address change management, and align with how services are actually delivered."
          />
          <PracticeAreaSection
            id="data"
            index={3}
            title="Data Use & Data Licensing"
            description="We structure data access, licensing, and commercialization arrangements for companies working with clinical, real-world, and healthcare data."
            approach="Data transactions require precision around permitted uses, aggregation rights, regulatory constraints, and return or destruction obligations. We help you build frameworks that enable commercial value while maintaining compliance."
          />
          <PracticeAreaSection
            id="life-sciences"
            index={4}
            title="Life Sciences Transactions"
            description="We handle technology and data agreements with pharmaceutical, biotech, and clinical research organizations—understanding the procurement processes, compliance requirements, and regulatory frameworks that shape these relationships."
            approach="Our clients sell into large pharma enterprises with complex buying processes. We know how to navigate vendor qualification, audit provisions, inspection readiness requirements, and the other terms unique to this industry."
          />
          <PracticeAreaSection
            id="ip"
            index={5}
            title="Intellectual Property Allocation"
            description="We structure IP ownership and licensing provisions in technology transactions—addressing background IP, deliverables, improvements, and the rights that matter most to technology vendors."
            approach="IP allocation is often the most negotiated part of life sciences technology agreements. We help protect your core technology while enabling the commercial relationships your business depends on."
          />
          <PracticeAreaSection
            id="privacy"
            index={6}
            title="Privacy & Data Protection"
            description="We draft data protection provisions in commercial agreements—data processing terms, HIPAA business associate agreements, GDPR requirements, and cross-border transfer mechanisms."
            approach="Privacy law continues to evolve. We build provisions that address current requirements while providing flexibility for regulatory changes, particularly for companies handling clinical or patient-level data."
          />
          <PracticeAreaSection
            id="vendor-risk"
            index={7}
            title="Vendor-Side Risk Allocation"
            description="We help technology vendors navigate the risk allocation provisions that enterprise customers push—understanding which terms matter, which can be accepted, and how to structure appropriate protections."
            approach="Large pharmaceutical and biotech customers often start with aggressive positions on liability, indemnification, and insurance. We know the patterns and help you respond with positions grounded in your actual risk profile."
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-law">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl text-navy mb-2">Have a specific transaction to discuss?</h2>
              <p className="text-gray-600">We work with general counsel, founders, and commercial teams on their most important deals.</p>
            </div>
            <Link to="/contact" className="btn-navy whitespace-nowrap" data-testid="practice-cta-btn">
              Discuss Your Matter <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
